/**
 * Shared 24h freshness policy for the persisted school selection.
 *
 * The selection lives in localStorage under a per-brand key. Historically it
 * never expired. We now stamp every write with `savedAt` and treat any
 * selection without a fresh stamp as expired, so a customer returning after
 * 24h — or one holding a legacy entry written before this policy existed — is
 * asked to pick their school again (and the configurator pages force the
 * SchoolSelector open; see hooks/useForceSchoolSelection).
 *
 * All brand SchoolSelector wrappers and readers (hooks/useSchoolSelection,
 * services/schoolSession) go through these helpers so the policy stays uniform
 * across the four localStorage shapes.
 */

export const SCHOOL_SELECTION_TTL_MS = 24 * 60 * 60 * 1000;

/** Attach the freshness stamp to a selection payload before persisting. */
export function stampSchoolSelection(payload) {
  return { ...(payload || {}), savedAt: Date.now() };
}

/** True when a parsed selection carries a stamp within the TTL window. */
export function isSchoolSelectionFresh(parsed) {
  const t = Number(parsed?.savedAt);
  return Number.isFinite(t) && (Date.now() - t) < SCHOOL_SELECTION_TTL_MS;
}

/**
 * Read + JSON-parse a stored selection, enforcing the TTL: an expired or
 * unparseable entry is removed and null is returned. Returns the parsed object
 * (as written, incl. `savedAt`) when still fresh.
 */
export function readFreshSelection(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isSchoolSelectionFresh(parsed)) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Same-tab change notification. The `storage` DOM event only fires in OTHER
 * tabs, so a class change made in the open popup never reached the hooks
 * reading the selection (useSchoolClass / useSchoolSelection) — the page kept
 * showing the previous class. Every selection write/clear dispatches this;
 * the reader hooks listen for it alongside `storage`.
 */
export const SCHOOL_SELECTION_EVENT = 'school-selection-updated';

export function notifySchoolSelectionUpdated(key) {
  try {
    window.dispatchEvent(new CustomEvent(SCHOOL_SELECTION_EVENT, { detail: { key } }));
  } catch { /* non-browser context — ignore */ }
}
